import { Injectable } from '@angular/core';
import { inject } from '@angular/core';
import { LoggingService } from './logging.service';

@Injectable({
  providedIn: 'root'
})
export class CameraPermissionService {
  private logger = inject(LoggingService);

  async requestCameraPermission(): Promise<boolean> {
    try {
      // Check if we're in a secure context (required for getUserMedia)
      if (!window.isSecureContext) {
        this.logger.warn('[Camera] Not in secure context (HTTPS required)');
        return false;
      }

      // Check if getUserMedia is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        this.logger.warn('[Camera] getUserMedia not available');
        return false;
      }

      // Request camera permission
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment' },
        audio: false 
      });
      
      // Stop the stream once we have permission
      stream.getTracks().forEach(track => track.stop());
      
      this.logger.info('[Camera] Permission granted');
      return true;
    } catch (error) {
      this.logger.error('[Camera] Permission denied or error', error);
      
      // Check specific error types
      if (error instanceof DOMException) {
        if (error.name === 'NotAllowedError') {
          this.logger.error('[Camera] User denied camera permission');
        } else if (error.name === 'NotFoundError') {
          this.logger.error('[Camera] No camera found');
        } else if (error.name === 'NotReadableError') {
          this.logger.error('[Camera] Camera is already in use');
        } else if (error.name === 'OverconstrainedError') {
          this.logger.error('[Camera] Camera constraints cannot be satisfied');
        } else if (error.name === 'SecurityError') {
          this.logger.error('[Camera] Security error - HTTPS required');
        }
      }
      
      return false;
    }
  }

  async checkCameraPermission(): Promise<PermissionState | null> {
    try {
      if ('permissions' in navigator) {
        const result = await navigator.permissions.query({ name: 'camera' as PermissionName });
        return result.state;
      }
    } catch (error) {
      this.logger.warn('[Camera] Unable to check permission status', error);
    }
    return null;
  }
}